import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.div`
  font-size: 16px;
  color: #666;
`;

const ActionButton = styled.button`
  background-color: ${(props) =>
    props.danger ? "#f44336" : props.primary ? "#6ed216" : "#f5f5f5"};
  color: ${(props) => (props.danger || props.primary ? "white" : "#333")};
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  margin-left: 10px;

  &:hover {
    background-color: ${(props) =>
      props.danger ? "#d32f2f" : props.primary ? "#5cb70f" : "#e0e0e0"};
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const BackButton = styled(ActionButton)`
  margin-bottom: 20px;
`;

const UsersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }

  &:hover {
    background-color: #f0f0f0;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  font-weight: 500;
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const SuccessContainer = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 20px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
`;

const ModalTitle = styled.h2`
  margin-top: 0;
  color: #333;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

function AdminUsersPage({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteModal, setDeleteModal] = useState({ show: false, userId: null });
  const navigate = useNavigate();

  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiUrl}/api/auth/users`);
      setUsers(response.data.users || []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      } else {
        setError(`Erreur lors du chargement des utilisateurs: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, navigate, onLogout]);

  useEffect(() => {
    // Vérifier si l'utilisateur est admin
    if (user && user.role !== "admin") {
      navigate("/");
      return;
    }

    fetchUsers();
  }, [user, navigate, fetchUsers]);

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${apiUrl}/api/auth/users/${userId}`);
      setSuccess("Utilisateur supprimé avec succès");
      setDeleteModal({ show: false, userId: null });
      fetchUsers();
    } catch (err) {
      setError(`Erreur lors de la suppression: ${err.message}`);
      setDeleteModal({ show: false, userId: null });
    }
  };

  const showDeleteModal = (userId) => {
    setDeleteModal({ show: true, userId });
  };

  const hideDeleteModal = () => {
    setDeleteModal({ show: false, userId: null });
  };

  return (
    <PageContainer>
      <BackButton onClick={() => navigate("/")}>← Retour</BackButton>

      <Header>
        <Title>Gestion des utilisateurs</Title>
        <Subtitle>Gérez les comptes utilisateurs et leurs permissions</Subtitle>
      </Header>

      {error && <ErrorContainer>{error}</ErrorContainer>}
      {success && <SuccessContainer>{success}</SuccessContainer>}

      <ActionButton primary onClick={() => navigate("/register")}>
        + Ajouter un utilisateur
      </ActionButton>

      {loading ? (
        <LoadingContainer>Chargement des utilisateurs...</LoadingContainer>
      ) : (
        <UsersTable>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Nom d'utilisateur</TableHeaderCell>
              <TableHeaderCell>Rôle</TableHeaderCell>
              <TableHeaderCell>Nom du commercial</TableHeaderCell>
              <TableHeaderCell>Identifiant Xandr</TableHeaderCell>
              <TableHeaderCell>Date de création</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <tbody>
            {users.map((user) => (
              <TableRow key={user._id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>
                  {user.role === "admin" ? "Administrateur" : "Commercial"}
                </TableCell>
                <TableCell>{user.salesPersonName || "-"}</TableCell>
                <TableCell>{user.xandrUsername}</TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleDateString("fr-FR")}
                </TableCell>
                <TableCell>
                  <ActionButton
                    onClick={() => navigate(`/edit-user/${user._id}`)}
                  >
                    Modifier
                  </ActionButton>
                  <ActionButton
                    danger
                    onClick={() => showDeleteModal(user._id)}
                  >
                    Supprimer
                  </ActionButton>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </UsersTable>
      )}

      {deleteModal.show && (
        <Modal>
          <ModalContent>
            <ModalTitle>Confirmer la suppression</ModalTitle>
            <p>Êtes-vous sûr de vouloir supprimer cet utilisateur ?</p>
            <p>Cette action est irréversible.</p>
            <ModalButtons>
              <ActionButton onClick={hideDeleteModal}>Annuler</ActionButton>
              <ActionButton
                danger
                onClick={() => handleDeleteUser(deleteModal.userId)}
              >
                Supprimer
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
}

export default AdminUsersPage;
